import React, { useEffect, useRef } from 'react';
import './Biography.css';

const Biography: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.hector-fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="biography-section modern-bio-layout" id="biography" ref={sectionRef}>
      {/* Modern Background Effects */}
      <div className="bio-background-effects">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="floating-particles">
          {[...Array(12)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`}></div>
          ))}
        </div>
      </div>

      <div className="modern-bio-container">
        {/* Hero Section */}
        <div className="bio-hero hector-fade-in-up">
          <div className="hero-content">
            <div className="hero-badge">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.5 7.5L18 9L13.5 10.5L12 15L10.5 10.5L6 9L10.5 7.5L12 2Z" fill="currentColor"/>
              </svg>
              <span>Master Artist</span>
            </div>
            <h1 className="hero-title">The Life & Vision of Hector Díaz</h1>
            <p className="hero-subtitle">
              Transforming downtown Tacoma through profound exploration of light, shadow, and celestial wonder
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="bio-main-grid hector-fade-in-up">
          <div className="content-panel primary-story">
            <div className="panel-header">
              <div className="panel-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15 9L22 9L17 14L19 22L12 18L5 22L7 14L2 9L9 9L12 2Z" fill="currentColor"/>
                </svg>
              </div>
              <h2>Master of Nocturnal Beauty</h2>
            </div>
            <div className="story-content">
              <p className="lead-paragraph">
                Hector Díaz emerged as one of Tacoma's most influential artists, transforming the downtown cultural landscape
                through his profound exploration of light, shadow, and celestial wonder.
              </p>
              <p>
                His work, characterized by layered horizons and atmospheric depth, captured the mysterious beauty of nocturnal landscapes.
                Based in downtown Tacoma, Hector operated his gallery as both an artistic sanctuary and a community hub,
                fostering the next generation of artists while showcasing his distinctive "Art of the Future" collection.
              </p>
            </div>
          </div>

          <div className="featured-image-panel">
            <div className="modern-image-container">
              <div className="image-frame">
                <img
                  src="/images/newsclippings/IMG_1203.png"
                  alt="Hector Diaz in his studio"
                  className="featured-image"
                />
                <div className="image-gradient-overlay"></div>
              </div>
              <div className="image-metadata">
                <div className="metadata-badge">Featured Story</div>
                <p className="image-caption">
                  "Downtown revitalization plans have helped gallery owner Hector Diaz"
                </p>
                <div className="caption-source">The News Tribune</div>
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy Quote */}
        <div className="philosophy-section hector-fade-in-up">
          <div className="quote-container modern-quote">
            <div className="quote-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
              </svg>
            </div>
            <blockquote className="philosophy-quote">
              "Through art, I strive to capture the romance and mystery of the night,
              where earth meets sky in perfect harmony"
            </blockquote>
            <div className="quote-attribution">
              <div className="attribution-line"></div>
              <cite>Hector Díaz</cite>
            </div>
          </div>
        </div>

        {/* Impact Areas */}
        <div className="impact-areas hector-fade-in-up">
          <div className="section-header">
            <h2 className="section-title">Artistic Impact & Legacy</h2>
            <div className="title-accent"></div>
          </div>

          <div className="impact-grid">
            <div className="impact-card youth-programs">
              <div className="card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4C18.2 4 20 5.8 20 8C20 10.2 18.2 12 16 12C13.8 12 12 10.2 12 8C12 5.8 13.8 4 16 4ZM8 4C10.2 4 12 5.8 12 8C12 10.2 10.2 12 8 12C5.8 12 4 10.2 4 8C4 5.8 5.8 4 8 4ZM8 6C6.9 6 6 6.9 6 8C6 9.1 6.9 10 8 10C9.1 10 10 9.1 10 8C10 6.9 9.1 6 8 6ZM16 6C14.9 6 14 6.9 14 8C14 9.1 14.9 10 16 10C17.1 10 18 9.1 18 8C18 6.9 17.1 6 16 6ZM8 13C10.67 13 16 14.33 16 17V20H0V17C0 14.33 5.33 13 8 13ZM16 13C16.22 13 16.48 13.01 16.77 13.03C17.58 13.54 18 14.23 18 17V20H24V17C24 14.33 18.67 13 16 13Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>New DAY - Diaz Art for Youth</h3>
              <p>
                Mentored young artists, providing tools and inspiration to express creativity
                and find their voice in the artistic community.
              </p>
              <div className="card-accent"></div>
            </div>

            <div className="impact-card cultural-influence">
              <div className="card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2ZM8 16L9 19L12 17.5L15 19L16 16L19 15L17.5 12L19 9L16 8L15 5L12 6.5L9 5L8 8L5 9L6.5 12L5 15L8 16Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Cultural Renaissance</h3>
              <p>
                Recognized with the City of Destiny Award, his gallery became a beacon for artistic expression,
                inspiring a generation of Pacific Northwest artists.
              </p>
              <div className="card-accent"></div>
            </div>

            <div className="impact-card artistic-legacy">
              <div className="card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9,11H15L13,9L15,7H9L11,9L9,11ZM12,2C17.5,2 22,6.5 22,12S17.5,22 12,22 2,17.5 2,12 6.5,2 12,2M12,4C7.6,4 4,7.6 4,12S7.6,20 12,20 20,16.4 20,12 16.4,4 12,4ZM12,6C15.3,6 18,8.7 18,12S15.3,18 12,18 6,15.3 6,12 8.7,6 12,6Z" fill="currentColor"/>
                </svg>
              </div>
              <h3>Art of the Future</h3>
              <p>
                Created a unique aesthetic vocabulary blending traditional landscapes with futuristic vision,
                exploring themes of hope, transformation, and eternal harmony.
              </p>
              <div className="card-accent"></div>
            </div>
          </div>
        </div>

        {/* Media Coverage */}
        <div className="media-coverage-modern hector-fade-in-up">
          <div className="section-header">
            <h2 className="section-title">Press & Recognition</h2>
            <div className="title-accent"></div>
          </div>

          <div className="media-showcase">
            <div className="media-item featured-media">
              <div className="media-frame">
                <img src="/images/newsclippings/IMG_1204.png" alt="Art galleries coverage" />
                <div className="media-overlay">
                  <div className="overlay-content">
                    <span className="media-type">Feature Story</span>
                    <h4>Tacoma's Evolving Art Scene</h4>
                  </div>
                </div>
              </div>
            </div>

            <div className="media-item featured-media">
              <div className="media-frame">
                <img src="/images/newsclippings/IMG_1207.png" alt="Finding Light in Darkness" />
                <div className="media-overlay">
                  <div className="overlay-content">
                    <span className="media-type">Artist Profile</span>
                    <h4>"Finding Light in the Darkness"</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Biography;